import {
  Chip,
  FormControl,
  InputBase,
  InputLabel,
} from '@material-ui/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';
import SearchFilter from 'store/models/SearchFilter';
import {
  renderClearButton,
  SIconButton,
} from 'components/workdesk/search-bar/ClearableSingleTextField';

interface MultiTextFieldProps {
  label: string;
  value: string[];
  onChange: (...args) => any;
  disabled?: boolean;
}

const getToDeleteIndex = (
  selectedChipAtIndex: number,
  valuesLength: number,
): number => {
  let result;
  if (!valuesLength) {
    result = -1;
  } else {
    if (selectedChipAtIndex === -1) {
      result = valuesLength - 1;
    } else if (selectedChipAtIndex < valuesLength - 1) {
      result = selectedChipAtIndex;
    } else {
      result = selectedChipAtIndex;
    }
  }
  return result;
};

const MultiTextField: React.FC<MultiTextFieldProps> = ({
  label,
  value = [],
  onChange,
  disabled,
}) => {
  const [textValue, setTextValue] = useState(
    // SearchFilter.joinStrings(value),
    '',
  );

  const [isEditingAtIndex, setIsEditingAtIndex] = useState(-1);
  const [onFocus, setOnFocus] = useState(false);
  const [values, setValues] = useState<any[]>(value);

  const shouldShowClear = useMemo(
    () => !!textValue || !!(values && values.length),
    [textValue, values],
  );

  const prevValuesRef = useRef(values);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOnChange = useCallback(onChange, []);

  useEffect(() => {
    stableOnChange({ target: { value: values } });
  }, [values, stableOnChange]);

  /**
   * for reset (same as in MultiAutocompleteWithSelector)
   */
  useEffect(() => {
    if (
      value &&
      Array.isArray(value) &&
      JSON.stringify(value) !== JSON.stringify(prevValuesRef.current)
    ) {
      setValues(value);
      prevValuesRef.current = value;
    }
  }, [value]);

  const handleTextChange = (
    event: React.ChangeEvent<{ value: string }>,
  ) => {
    const val = event.target.value;
    if (val && val === ',') {
      // wait
    } else {
      setTextValue(val);
    }
  };

  const handleSetOnFocus = (val: boolean) => {
    setOnFocus(val);
    // console.debug(`handleSetOnFocus: `, val, inputRef?.current);
    if (val && inputRef?.current) {
      (inputRef.current as any).focus();
    } else if (!val) {
      handleOnBlur();
    }
  };

  const _handleSplitText = (e?, checkKey?) => {
    if (checkKey && !(e.key === 'Enter' || e.key === ',')) return;
    const val = textValue;
    const index = isEditingAtIndex;
    if (val !== '') {
      const newValues =
        index === -1
          ? values.concat(SearchFilter.splitString(val))
          : values.map((v, i) => (i !== index ? v : val));
      setValues(newValues);
      setTextValue('');
    }
    setIsEditingAtIndex(-1);
  };

  const handleOnBlur = () => {
    _handleSplitText();
    setSelectedChipAtIndex(-1);
  };

  const handleTextKeyDown = (e) => {
    _handleSplitText(e, true);
  };

  const handleClear = () => {
    setValues([]);
  };

  const handleDeleteChipAtIndex = (index: number) => {
    if (index > values.length - 1) {
      throw Error(`invalid index ${index}`);
    }
    setValues(values.filter((val, i) => i !== index));
  };
  const handleDoubleClickChipAtIndex = (index: number, value) => {
    setIsEditingAtIndex(index);
    setTextValue(value);
  };
  const inputRef = useRef(null);
  const formControlRef = useRef(null);

  const [selectedChipAtIndex, setSelectedChipAtIndex] = useState(-1);

  const handleFormKeydown = (e) => {
    if (textValue === '') {
      console.debug(`selectedChipAtIndex: ${selectedChipAtIndex}`);

      console.debug(`handleFormKeydown e.Key`, e.key);
      if (e.key === 'ArrowLeft' && selectedChipAtIndex !== 0) {
        setSelectedChipAtIndex(
          selectedChipAtIndex === -1
            ? values.length - 1
            : selectedChipAtIndex - 1,
        );
      } else if (e.key === 'ArrowRight') {
        if (
          selectedChipAtIndex < values.length - 1 &&
          selectedChipAtIndex !== -1
        ) {
          // at not last chip
          setSelectedChipAtIndex(selectedChipAtIndex + 1);
        } else if (
          selectedChipAtIndex === values.length - 1 &&
          selectedChipAtIndex !== -1
        ) {
          // at last chip
          setSelectedChipAtIndex(-1);
        }
      } else if (e.key === 'Backspace') {
        const toDeleteIndex = getToDeleteIndex(
          selectedChipAtIndex,
          values.length,
        );
        if (toDeleteIndex < -1)
          throw Error(`wrong toDeleteIndex ${toDeleteIndex}`);
        const afterSelectedChipIndex = getToDeleteIndex(
          toDeleteIndex - 1,
          values.length ? values.length - 1 : 0,
        );
        // console.debug(
        //   `afterSelectedChipIndex: `,
        //   afterSelectedChipIndex,
        //   'toDeleteIndex',
        //   toDeleteIndex,
        //   'selectedChipAtIndex',
        //   selectedChipAtIndex,
        // );
        // delete
        handleDeleteChipAtIndex(toDeleteIndex);
        setSelectedChipAtIndex(afterSelectedChipIndex);
      }
    }
  };

  // useOutsideAlerter(formControlRef, () => {
  //   handleSetOnFocus(false);
  // });
  return (
    <SInside>
      <SFormControl
        ref={formControlRef}
        className={`${onFocus ? 'is-focus' : ''} ${
          disabled ? 'is-disabled' : ''
        }`}
        onClick={() => handleSetOnFocus(true)}
        onFocus={() => handleSetOnFocus(true)}
        onBlur={() => handleSetOnFocus(false)}
        disabled={disabled}
        onKeyDown={(e) => handleFormKeydown(e)}
      >
        {label && <SInputLabel shrink>{label}</SInputLabel>}
        <SMultiTextField
          className={`${onFocus ? 'is-focus' : ''} ${
            shouldShowClear ? 'show-close' : ''
          }`}
        >
          <SChips className={`${onFocus ? 'is-focus' : ''} `}>
            {values.map((v, i) => {
              return i !== isEditingAtIndex ? (
                <SChip
                  size="small"
                  key={`${i}-${v}`}
                  className={`${
                    i === selectedChipAtIndex ? 'is-highlighted' : ''
                  }`}
                  label={v}
                  onDelete={() => {
                    handleDeleteChipAtIndex(i);
                  }}
                  onFocus={() => handleSetOnFocus(true)}
                  onDoubleClick={(e) => {
                    handleDoubleClickChipAtIndex(i, v);
                  }}
                />
              ) : (
                <SEditingChip
                  value={textValue}
                  onChange={handleTextChange}
                  onKeyDown={(e) => handleTextKeyDown(e)}
                />
              );
            })}
          </SChips>
          {isEditingAtIndex === -1 && (
            <STextField
              value={textValue}
              onChange={handleTextChange}
              onKeyDown={(e) => handleTextKeyDown(e)}
              inputRef={inputRef}
            />
          )}
          {renderClearButton(handleClear)}
        </SMultiTextField>
      </SFormControl>
    </SInside>
  );
};

// const MultiTextFieldContainer = React.forwardRef((props, ref) => (
//   <MultiTextField {...props} ref={ref} />
// ));

export default MultiTextField;

const STextField: any = styled(InputBase)`
  /* & .MuiOutlinedInput-input {
    padding: 15.5px 14px;
    font-size: ${(props) => props.theme.typography.fontSize.regular};
  }
  & .MuiOutlinedInput-root {
    &:hover fieldset {
      border-color: ${(props) => props.theme.palette.primary.main};
    }
  } */
`;
const SMultiTextField = styled.div.attrs((props) => ({
  className: props.className,
}))`
  margin-top: 15px;
  &:not(.is-focus) {
    display: inline-flex;
  }
  &.show-close ${SIconButton} {
    visibility: hidden;
    opacity: 0;
    transition: visibility 0.15s, opacity 0.15s;
  }
  &.show-close:hover ${SIconButton} {
    visibility: visible;
    opacity: 1;
  }
  &:not(.show-close) ${SIconButton} {
    visibility: hidden;
    opacity: 0;
  }
`;
const SFormControl = styled(FormControl).attrs((props) => ({
  className: props.className,
}))`
  & .MuiSelect-select:focus {
    background-color: transparent;
    /* border: ${(props) => props.theme.palette.primary.main}; */
  }
  &.MuiFormControl-root {
    flex: 1;
    min-height: 3rem;
    min-width: 100%;
    /* max-width: 302px; // not ideal to use fix width */
    flex-wrap: nowrap;
    border: solid 1px
      ${(props) => props.theme.palette.common.borderGreyDarker};
    border-radius: 4px;
    &.is-focus {
      border: solid 2px ${(props) => props.theme.palette.primary.main};
      /* let the text grow */
      position: absolute;
      /* width: 300px;
      height: 200px; */
      top: 0;
      left: 0;
      border-radius: 4px;
      background: ${(props) => props.theme.palette.background.paper};
      z-index: 3;
      overflow: unset;
    }
    overflow: hidden;
  }
  &.MuiFormControl-root:not(.is-disabled):hover {
    border: solid 1px ${(props) => props.theme.palette.primary.main};
  }
  & .MuiInput-underline:before {
    border-bottom: none;
  }
  & .MuiInput-underline:hover:not(.Mui-disabled):before {
    border-bottom: none;
  }
  & .MuiInput-underline:after {
    border-bottom: none;
  }
  & label + .MuiInput-formControl {
    margin-top: 10px;
  }
  & .MuiInput-formControl {
    margin-left: 5px;
  }
  & .MuiInputBase-root {
    flex: 1;
    width: 100%;
  }
`;
const SInputLabel = styled(InputLabel)`
  &.MuiInputLabel-root {
    background: white;
    margin-left: 5px;
    transform: translate(0, 2px) scale(0.75);
  }
  &.MuiFormLabel-root.Mui-focused {
    padding: 3px;
  }
`;
const SChips = styled.div.attrs((props) => ({
  className: props.className,
}))`
  display: inline-flex;
  flex-wrap: wrap;
  row-gap: 7px;
  margin-bottom: 7px;
  &.is-focus {
    flex-wrap: wrap;
    row-gap: 2px;
    max-width: unset;
  }
  &:not(.is-focus) {
    flex-wrap: nowrap;
    max-width: 17vw;
    overflow: hidden;
  }
`;
const SChip = styled(Chip).attrs((props) => ({
  className: props.className,
}))`
  &.MuiChip-root {
    /* height: 1.5rem; */
  }
  &.is-highlighted {
    background-color: #cccbcb;
  }
`;
const SInside = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  min-height: 3rem;
`;
const SEditingChip = styled(InputBase)``;
